import { useNotificationContext } from "@circle-react-shared/Notifications/NotificationContext";
import {
  useNotificationsCount,
  useResetNotificationCount,
} from "@circle-react-shared/Notifications/useHeaderNotificationsApi";
import { useNotificationsWidth } from "@circle-react-shared/Notifications/useNotificationsWidth";
import { PopoverPortalContext } from "@circle-react-shared/uikit/PopoverPortal";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useNotificationWebSocket } from "@circle-react/hooks/notifications/useNotificationWebSocket";
import { Popover as CompassPopover } from "@circleco/compass/components/Popover";
import { useLocation } from "@tanstack/react-router";
import { useState, useCallback, useEffect, useMemo } from "react";
import { useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";

import { HeaderIconButton } from "../HeaderIconButton";
import { formatNotificationsCount } from "../utils";
import { Banner } from "./Banner";
import { Body } from "./Body";
import { Header } from "./Header";
import {
  onNotificationReceive,
  type WebSocketNotificationEvent,
} from "./utils";

export const NotificationsPopover = () => {
  const {
    activeFilter,
    notificationCount,
    setNotificationCount,
    setNotificationTabCounters,
  } = useNotificationContext();

  const queryClient = useQueryClient();
  const { currentCommunityMember, currentCommunity, currentCommunitySettings } =
    usePunditUserContext();
  const isNotificationsTabV2Enabled = Boolean(
    currentCommunitySettings?.live_pulse_notifications_tab_v2_enabled,
  );

  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const popoverPortalContextValue = useMemo(
    () => ({ onClose: () => setIsOpen(false) }),
    [],
  );

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  useNotificationsCount(currentCommunity?.id, setNotificationTabCounters);
  const { mutate: resetNotificationCount } =
    useResetNotificationCount(setNotificationCount);

  useNotificationWebSocket(
    currentCommunityMember?.id as number,
    (eventData: WebSocketNotificationEvent) =>
      onNotificationReceive({
        eventData,
        queryClient,
        setNotificationCount,
        isNotificationsTabV2Enabled,
        activeFilter,
      }),
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (open && notificationCount > 0) {
        resetNotificationCount();
      }
    },
    [notificationCount, resetNotificationCount],
  );

  const { tabsContainerRef, tabsContainerWidth } = useNotificationsWidth();

  return (
    <CompassPopover
      trigger={
        <HeaderIconButton
          icon="bell"
          ariaLabel={t("header_v3.notifications")}
          hotkey="alt+n"
          counter={
            notificationCount > 0
              ? formatNotificationsCount(notificationCount)
              : undefined
          }
          isOpen={isOpen}
          data-testid="notifications-menu-popover-button"
        />
      }
      open={isOpen}
      onOpenChange={handleOpenChange}
      side="bottom"
      align="end"
      sideOffset={4}
      fillAvailableHeight
      className="max-h-[38rem] max-w-[45rem] p-0"
    >
      <PopoverPortalContext.Provider value={popoverPortalContextValue}>
        <div
          className="flex h-full w-fit flex-col"
          data-testid="notifications-drawer"
        >
          <Banner tabsContainerWidth={tabsContainerWidth} />
          <Header />
          <Body
            tabsContainerRef={tabsContainerRef}
            tabsContainerWidth={tabsContainerWidth}
          />
        </div>
      </PopoverPortalContext.Provider>
    </CompassPopover>
  );
};
